import { axios } from '@/plugins/axios';
import merge from 'lodash/merge';
import Pager from '@/helpers/Pager';
import { Option, Pagination, Resource, Statement } from '@/helpers/Interfaces';
import { DateTime } from 'luxon';
import { DashboardAccessibilityType } from '@/helpers/Enums';

/**
 * DashboardsService
 */
export default class DashboardsService
{
    public static path = 'admin/dashboards';

    // ----------------------------------------

    public static async getList(filter: FilterModel, pager: Pager): Promise<Pagination<Resource<ListItemModel>>>
    {
        return (await axios.get<Pagination<Resource<ListItemModel>>>(`${this.path}`, {
            params: merge({}, filter, pager.data())
        })).data;
    }

    public static async fetch(id: number)
    {
        return (await axios.get<Resource<FormModel>>(`${this.path}/${id}`)).data;
    }

    public static async create(model: FormModel)
    {
        return (await axios.post<Resource<FormModel>>(`${this.path}`, model)).data;
    }

    public static async update(id: number, model: FormModel)
    {
        return (await axios.put<Statement>(`${this.path}/${id}`, model)).data;
    }

    public static async remove(id: number): Promise<Statement>
    {
        return (await axios.delete<Statement>(`${this.path}/${id}`)).data;
    }

    public static async copy(id: number): Promise<Statement>
    {
        return (await axios.post<Statement>(`${this.path}/${id}/copy`)).data;
    }

    // ----------------------------------------

    public static async getOptions(): Promise<Option<number>[]>
    {
        return (await axios.get<Option<number>[]>(`${this.path}/options`)).data;
    }

    public static async getDashboard(id: number)
    {
        return (await axios.get<DashboardModel>(`${this.path}/layout/${id}`)).data;
    }

    public static async getUserDashboard()
    {
        return (await axios.get<DashboardModel>(`${this.path}/layout`)).data;
    }

    public static async setUserDashboard(id: number)
    {
        return (await axios.post<Statement>(`${this.path}/layout/${id}`)).data;
    }

    // ----------------------------------------

    public static async getCategories()
    {
        return (await axios.get<Category[]>(`${this.path}/settings/categories`)).data;
    }

    public static async getWidgets(filter: WidgetsFilterModel)
    {
        return (await axios.get<Widget[]>(`${this.path}/settings/widgets`, {
            params: filter
        })).data;
    }

    public static async getWidget(widgetType: string): Promise<Widget>
    {
        return (await axios.get<Widget>(`${this.path}/settings/widgets/${widgetType}`)).data;
    }
}

export interface ListItemModel
{
    id: number;
    dateCreatedUtc: DateTime;
    createdBy: string;
    name: string;
    isDefault: boolean;
    isActive: boolean;
    accessibilityType: DashboardAccessibilityType;

}

export interface FilterModel
{
    name: string;
    createdBy?: number;
    onlyMine: boolean;
    isDefault: boolean;
    isActive: boolean;
    accessibilityType: DashboardAccessibilityType;
    dateCreatedFromUtc?: DateTime;
    dateCreatedToUtc?: DateTime;
}

export interface FormModel
{
    id: number,
    name: string;
    description: string;
    isDefault: boolean;
    isActive: boolean;
    accessibilityType: DashboardAccessibilityType;
    config: WidgetModel[];
}

export interface DashboardModel
{
    id: number,
    widgets: WidgetModel[];
}

export interface WidgetModel
{
    id: string;
    type: string;
    name: string;
    backgroundColor?: string;
    x?: number;
    y?: number;
    width: number;
    height: number;
    sourceConfig: Record<string, any>;
}

export interface Category
{
    id: string;
    name: string;
    icon: string;
}

export interface Widget
{
    type: string;
    name: string;
    description: string;
    icon: string;
    width: number;
    height: number;
    categories: string[];
}

export interface GridItemAttributes
{
    id: string;
    "gs-id": string;
    "gs-x": number;
    "gs-y": number;
    "gs-w": number;
    "gs-h": number;
    "gs-no-resize"?: boolean;
}

export interface WidgetsFilterModel
{
    search: string;
    categoryId: string;
}

